import { createServerFn } from "@tanstack/react-start";
import { getRequest, getRequestHeader, getRequestIP } from "@tanstack/react-start/server";
import { z } from "zod";

import {
  quickQuoteStep1Schema,
  quickQuoteStep2Schema,
  quickQuotePhotoSchema,
  quickQuotePhotosSchema,
  trackingSchema,
  friendlyParse,
} from "./quick-quote-schemas";
import { addressStepPatch, formTypeFromPath, trackingCells } from "./quick-quote-fields";
import { isQuickQuoteTest } from "./test-mode.server";
import { isPreviewHost } from "./lead-host.server";
import type { DeliverCtx } from "./quick-quote-deliver.server";
import type { FoundLead } from "./quick-quote-sinks.server";
import type { QueueRow } from "./quick-quote-queue.server";
import type { RescueRecord } from "./lead-rescue.server";

/**
 * Step 1 — buffer the lead in the Google Sheet. NOTHING is sent to Salesforce
 * yet: either the visitor completes step 2 (one complete lead) or the flush job
 * pushes this partial a few minutes later. Returns the lead id used on page 2.
 */
export const startQuickQuote = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => friendlyParse(quickQuoteStep1Schema, data))
  .handler(async ({ data }) => {
    const ip = getRequestIP({ xForwardedFor: true }) ?? null;
    const userAgent = getRequestHeader("user-agent") ?? "";
    const ctx = await leadContext();
    if (!ctx) {
      // Test mode creates no lead. It returns the Web-to-Lead fields this
      // lead would carry, built by the same code as a real send, so the
      // channel matrix check can assert the lead source per traffic channel.
      const { previewQuickQuoteBody } = await import("./quick-quote-w2l.server");
      const lead_id = crypto.randomUUID();
      const w2l = previewQuickQuoteBody({
        id: lead_id, step: 1,
        first_name: data.first_name, last_name: data.last_name, email: data.email,
        phone: data.phone, zip: data.zip, norwood: data.norwood || null,
        heard_about: data.heard_about ?? null,
        consent_contact: true, consent_terms: true, consent_sensitive: true,
        address1: null, address2: null, city: null, state: null, address_zip: null,
        page_path: data.page_path ?? null, referrer: data.referrer ?? null,
        tracking: { ...(data.tracking ?? {}), ...(data.ludwig ? { ludwig: data.ludwig } : {}) },
        photo_url: null, created_at: new Date().toISOString(),
        ip_address: ip, user_agent: userAgent.slice(0, 500),
        form_type: formTypeFromPath(data.page_path),
      });
      return { ok: true as const, lead_id, w2l };
    }

    // Turnstile and the bot checks (8 Oct 2026, quick-quote-bot.server.ts).
    // A token that does not verify no longer stops the lead by itself.
    // Fault tests use a Turnstile stand-in, so they never call siteverify.
    const { checkStep1, recordBotCheck } = await import("./quick-quote-bot.server");
    const { rawQueueDb } = await import("./quick-quote-queue.server");
    const { parseFaults } = await import("./quick-quote-sinks.server");
    const testFaults = ctx.sinks.test ? parseFaults(getRequestHeader("x-rh-fault")) : new Set<string>();
    const botEnv = {
      test: ctx.sinks.test,
      turnstileFault: testFaults.has("turnstile"),
      rateLimit: !ctx.sinks.test || testFaults.has("ratelimit"),
      db: rawQueueDb(),
      ip,
      cf: (getRequest() as Request & { cf?: unknown }).cf ?? null,
    };
    const bot = await checkStep1(data, botEnv);
    if (bot.kind === "legacy_reject") {
      console.warn("[quickQuote] turnstile rejected", bot.turnstile);
      throw new Error(`Verification failed (${bot.turnstile.replace(/^invalid:/u, "")}). Please try again.`);
    }

    const first_name = data.first_name;
    const last_name = data.last_name;
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const trackingBlob = {
      ...(data.tracking ?? {}),
      ...(data.ludwig ? { ludwig: data.ludwig } : {}),
    };

    const cells: Record<string, string> = {
      ...trackingCells(trackingBlob, { userAgent, gclid: null }),
      id,
      created_at: now,
      updated_at: now,
      step: "1",
      first_name,
      last_name,
      email: data.email,
      phone: data.phone,
      zip: data.zip,
      norwood: data.norwood || "",
      heard_about: data.heard_about ?? "",
      consent_contact: "TRUE",
      consent_terms: "TRUE",
      consent_sensitive: "TRUE",
      page_path: data.page_path ?? "",
      referrer: data.referrer ?? "",
      ip_address: ip ?? "",
      user_agent: userAgent.slice(0, 500),
      form_type: formTypeFromPath(data.page_path),
      tracking: JSON.stringify(trackingBlob),
      salesforce_status: "pending",
      salesforce_attempts: "0",
    };

    if (bot.kind === "rejected_bot") {
      // Kept in D1 for review, never delivered. The bot sees the normal
      // answer. If D1 cannot keep it, the lead goes on as unverified: a
      // lost real lead costs more than one more bot lead.
      if (await recordBotCheck(botEnv, id, bot, data.fill_ms, cells)) {
        console.warn(`[quickQuote] rejected_bot (${bot.reason}), lead=${id}`);
        return { ok: true as const, lead_id: id };
      }
    }
    if (bot.kind !== "verified") {
      const unverified = { kind: "unverified" as const, turnstile: bot.turnstile, ipHash: bot.ipHash, botScore: bot.botScore };
      await recordBotCheck(botEnv, id, unverified, data.fill_ms);
      console.warn(`[quickQuote] unverified lead accepted (${bot.turnstile}), lead=${id}`);
    }

    // Repeat send (14 Oct 2026), after the bot checks so a rejected bot never
    // gets an earlier lead's id. Visitors who finished the form often click
    // "Get a Quick Quote" again (confirmation page, header, footer) and send
    // the same details once or twice more, minutes later. Each send used to
    // be a new Sheet row and a new Salesforce lead (Brian Polis, 14 Oct:
    // "triple submissions"). Same email and phone within REPEAT_WINDOW_MS:
    // carry on with the earlier lead. Page 2 then adds to it, and the
    // existing rules decide whether Salesforce hears about the new data.
    const { REPEAT_WINDOW_MS } = await import("./quick-quote-queue.server");
    const repeat = await ctx.queue.findRecentRepeat(
      data.email,
      data.phone,
      new Date(Date.now() - REPEAT_WINDOW_MS).toISOString(),
    );
    if (repeat) {
      console.log(`[quickQuote] repeat send, reusing lead=${repeat.id} (created ${repeat.created_at}), no new row`);
      // Counted with the other blocked duplicates (qq-health duplicatesBlocked24h).
      await ctx.queue.bumpDuplicatesBlocked(repeat.id);
      return { ok: true as const, lead_id: repeat.id, repeat: true as const };
    }

    // Cookie banner A/B test (src/lib/banner-ab.server.ts): one count per new
    // lead, from exp_banner in its tracking. At most 1 s, never throws.
    if (!ctx.sinks.test) {
      const { countBannerLead } = await import("@/lib/banner-ab.server");
      await countBannerLead(trackingBlob);
    }

    // Write-ahead (6 Oct 2026): the lead goes to the D1 queue first. If the
    // Sheet append then fails, the lead is safe there and the replay job
    // writes the row later. If D1 is down or slow (3 s), the Sheet path runs
    // exactly as before.
    //
    // Zero loss (14 Oct 2026): if D1 AND the Sheet both fail, the lead goes
    // to the R2 rescue store (lead-rescue.server.ts) and the visitor goes on.
    // Only when R2 fails as well does the visitor see the error.
    const { captureFail } = await import("./lead-rescue.server");
    const failures: string[] = [];
    const queued = await ctx.queue.insert(id, cells);
    if (!queued) {
      failures.push(
        captureFail("d1", "qq", id, ctx.queue.available ? "queue insert failed or timed out" : "no D1 binding", ctx.sinks.test),
      );
    }
    try {
      await ctx.sinks.sheet.appendLead(cells);
      if (queued) await ctx.queue.update(id, { sheet_status: "done", sheet_error: null });
    } catch (err) {
      console.error(`[quickQuote] failed to buffer step 1, lead=${id}: ${shortErr(err)}`);
      failures.push(captureFail("sheet", "qq", id, err, ctx.sinks.test));
      if (queued) {
        await ctx.queue.sheetFailed(id, err);
        return { ok: true as const, lead_id: id };
      }
      await rescueOrThrow(ctx, { kind: "qq", id, step: 1, cells, failures, created_at: now }, COULD_NOT_SAVE_DETAILS);
    }

    return { ok: true as const, lead_id: id };
  });

/**
 * Step 2 — attach the address and deliver the lead to Salesforce immediately,
 * so an abandoned photo step still results in a complete lead. If the photo is
 * later uploaded (or skipped), the lead is re-posted with the photo URL.
 */
export const completeQuickQuote = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => friendlyParse(quickQuoteStep2Schema, data))
  .handler(async ({ data }) => {
    const ctx = await leadContext();
    if (!ctx) return { ok: true as const, delivery: "deferred" as const };

    const { found, queued, rescued } = await locateLead(data.lead_id, ctx);
    const tracking = mergeTracking(found?.lead.tracking ?? queued?.lead?.tracking ?? rescued?.cells?.tracking, data.tracking);
    // The address, and the hair-loss class when step 2 asked for it
    // (16 Oct 2026): see addressStepPatch.
    const patch = addressStepPatch(data, tracking);

    // Write-ahead: the address goes to the queue first. It is new data for
    // Salesforce, so the queue's Salesforce status goes back to pending.
    const q = queued
      ? await ctx.queue.mergeLead(data.lead_id, patch, {
          step: 2,
          sheet_status: "pending",
          last_step_at: new Date().toISOString(),
          ...(queued.salesforce_status === "sending" ? {} : { salesforce_status: "pending" }),
        })
      : null;

    if (found) {
      try {
        await ctx.sinks.sheet.updateLeadCells(found.rowNumber, patch);
        if (q && queued?.sheet_status === "done") {
          await ctx.queue.update(data.lead_id, { sheet_status: "done", sheet_error: null });
        }
      } catch (err) {
        console.error(`[quickQuote] failed to save step 2, lead=${data.lead_id}: ${shortErr(err)}`);
        const { captureFail } = await import("./lead-rescue.server");
        const sheetFail = captureFail("sheet", "qq", data.lead_id, err, ctx.sinks.test);
        if (!q) {
          // The Sheet has step 1 but the address could not be written, and
          // D1 is down: keep the whole lead with its address in R2.
          const d1Fail = captureFail("d1", "qq", data.lead_id, queued ? "queue merge failed or timed out" : "no queue row", ctx.sinks.test);
          await rescueOrThrow(
            ctx,
            {
              kind: "qq",
              id: data.lead_id,
              step: 2,
              cells: { ...withoutBookkeeping(found.lead as unknown as Record<string, string>), ...patch },
              failures: [d1Fail, sheetFail],
              created_at: found.lead.created_at || new Date().toISOString(),
            },
            COULD_NOT_SAVE_ADDRESS,
          );
          return { ok: true as const, delivery: "deferred" as const };
        }
        // Saved in the queue; the replay job writes it to the Sheet.
        await ctx.queue.sheetFailed(data.lead_id, err);
      }
    } else if (!q) {
      // Neither the Sheet nor D1 has this lead right now. If step 1 was
      // rescued to R2 (and D1 is still down), add the address to that object.
      if (!rescued) throw new Error(COULD_NOT_SAVE_ADDRESS);
      await rescueOrThrow(
        ctx,
        {
          ...rescued,
          step: Math.max(2, rescued.step ?? 1),
          cells: { ...(rescued.cells ?? {}), ...patch },
          failures: [...rescued.failures, "d1: still unavailable at step 2"],
        },
        COULD_NOT_SAVE_ADDRESS,
      );
      return { ok: true as const, delivery: "deferred" as const };
    }

    // The flush already sent the step-1 partial: Salesforce has this lead.
    // Mark the address as kept in the Sheet now, so no flush (the Lovable one
    // runs older code) re-posts it as a second Salesforce lead if the visitor
    // leaves before the photo step. See LATE_DATA_MARKER.
    const { dataSignature, lateRepostAllowed, recordLateDataKept } = await import("./quick-quote-deliver.server");
    const alreadySent = found?.lead.salesforce_status === "sent" || (queued?.salesforce_signature ?? null) !== null;
    if (alreadySent && !lateRepostAllowed()) {
      const merged = { ...(found?.lead ?? {}), ...(queued?.lead ?? {}), ...patch } as Parameters<typeof dataSignature>[0];
      await recordLateDataKept(ctx, data.lead_id, found, dataSignature(merged));
    }

    // Do NOT deliver here. The photo step (upload or skip) happens seconds
    // later and delivers once; if the visitor abandons it, the 3-minute flush
    // job pushes the row. Delivering here as well produced duplicate leads.
    return { ok: true as const, delivery: "deferred" as const };
  });

/**
 * Step 2b — upload a patient photo to Google Drive, store the link in the
 * sheet, and deliver the complete lead to Salesforce.
 */
export const uploadQuickQuotePhoto = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => friendlyParse(quickQuotePhotoSchema, data))
  .handler(async ({ data }) => {
    if (isQuickQuoteTest() || isPreviewHost()) {
      return { ok: true as const, photo_url: "https://test.invalid/photo.jpg", delivery: "skipped" as const };
    }
    const { findLead, updateLeadCells } = await import("./quick-quote-sheet.server");
    const { uploadPhotoToDrive } = await import("./quick-quote-drive.server");

    const found = await findLead(data.lead_id);
    if (!found) {
      throw new Error("We couldn't find your quote. Please start again.");
    }

    let tracking: Record<string, string> = {};
    try {
      tracking = found.lead.tracking ? JSON.parse(found.lead.tracking) : {};
    } catch {
      tracking = {};
    }
    tracking = { ...tracking, ...(data.tracking ?? {}) };

    const uploaded = await uploadPhotoToDrive(
      found.lead.email || data.lead_id,
      data.photo_base64,
      data.photo_mime,
    );

    await updateLeadCells(found.rowNumber, {
      ...trackingCells(tracking),
      photo_url: uploaded.file_url,
      photo_file_id: uploaded.file_id,
      tracking: JSON.stringify(tracking),
    });

    // The photo is delivered to Salesforce as a Drive link on
    // Patient_Photo_URL__c — no email to client relations.
    const { deliverQuickQuoteLead } = await import("./quick-quote-deliver.server");
    // Delivery re-posts only when the row holds address/photo data that was not
    // in the last post — the photo alone can never create a duplicate entry.
    const result = await deliverQuickQuoteLead(data.lead_id);

    return { ok: true as const, photo_url: uploaded.file_url, delivery: result.outcome };
  });

/**
 * Multi-photo upload — the visitor can pick up to 6 photos. Each goes to
 * Google Drive, all links are stored (newline-separated) in the sheet's
 * photo_url cell, and the complete lead is delivered once to Salesforce.
 */
export const uploadQuickQuotePhotos = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => friendlyParse(quickQuotePhotosSchema, data))
  .handler(async ({ data }) => {
    const ctx = await leadContext();
    if (!ctx) {
      return { ok: true as const, photo_urls: data.photos.map(() => "https://test.invalid/photo.jpg"), delivery: "skipped" as const };
    }

    const { found, queued, rescued } = await locateLead(data.lead_id, ctx);
    // Only the R2 rescue holds the lead (D1 and the Sheet still down): there
    // is nowhere to record a photo link yet. The lead itself is safe; the
    // visitor is asked to try the photos again.
    if (!found && !queued && rescued) {
      const { captureFail } = await import("./lead-rescue.server");
      captureFail("d1", "qq", data.lead_id, "photo step while the lead is only in the R2 rescue", ctx.sinks.test);
      throw new Error("We couldn't save your photos just now. Please try again in a minute.");
    }
    const base = {
      email: found?.lead.email || queued?.lead?.email || "",
      tracking: found?.lead.tracking ?? queued?.lead?.tracking,
      photo_url: found?.lead.photo_url || queued?.lead?.photo_url || "",
    };
    const tracking = mergeTracking(base.tracking, data.tracking);

    const emailOrId = base.email || data.lead_id;
    // Drive file ids already stored: the longer list of the Sheet and the queue
    // (one of them may have missed a write while its store was down).
    const sheetIds = idList(found?.lead.photo_file_id);
    const queueIds = idList(queued?.lead?.photo_file_id);
    const knownIds = sheetIds.length >= queueIds.length ? sheetIds : queueIds;
    // Photos waiting in the R2 fallback keep their numbers too.
    const waiting = queued ? await ctx.queue.pendingPhotoCount(data.lead_id) : 0;
    const priorCount = knownIds.length + waiting;

    // All of a lead's photos go into one folder named after their email, and
    // that single folder link is what Salesforce receives.
    let folder: { folder_id: string; folder_url: string } | null = null;
    let folderError: unknown = null;
    try {
      folder = await ctx.sinks.drive.ensureLeadFolder(emailOrId);
    } catch (err) {
      folderError = err;
    }
    const uploadedUrls: string[] = [];
    const uploadedIds: string[] = [];
    let fallbackCount = 0;
    for (let i = 0; i < data.photos.length; i += 1) {
      const photo = data.photos[i]!;
      const index = priorCount + i + 1;
      // A retry (attempt > 1) of a photo with an id first looks for the copy
      // an earlier try stored, so a lost answer never makes a duplicate
      // (7 Oct 2026). A first try skips the lookup to stay fast.
      const retry = !!photo.photo_id && (photo.attempt ?? 1) > 1;
      try {
        if (!folder) throw folderError;
        const uploaded = await ctx.sinks.drive.uploadPhotoToDrive(
          emailOrId,
          photo.photo_base64,
          photo.photo_mime,
          index,
          folder.folder_id,
          { photoId: photo.photo_id, lookup: retry },
        );
        uploadedUrls.push(uploaded.file_url);
        // A retry can return a file whose id the Sheet or queue already holds.
        if (!knownIds.includes(uploaded.file_id) && !uploadedIds.includes(uploaded.file_id)) {
          uploadedIds.push(uploaded.file_id);
        }
      } catch (err) {
        // Drive failed after its own retries (6 Oct 2026): keep the photo in
        // the private R2 bucket and let the replay job move it to Drive. The
        // lead goes on. Without a queue row there is nowhere to index it, and
        // the visitor sees the error as before.
        console.error(`[quickQuote] drive upload failed, lead=${data.lead_id}: ${shortErr(err)}`);
        const stored = queued
          ? await ctx.queue.storeFallbackPhoto(data.lead_id, index, photo.photo_base64, photo.photo_mime, photo.photo_id)
          : false;
        if (!stored) throw err;
        fallbackCount += 1;
      }
    }

    const allIds = [...knownIds, ...uploadedIds].join(",");
    const patch: Record<string, string> = {
      ...trackingCells(tracking),
      tracking: JSON.stringify(tracking),
    };
    if (folder && uploadedIds.length > 0) {
      patch.photo_url = folder.folder_url;
      patch.photo_file_id = allIds;
    }

    const photoUrlChanged = !!patch.photo_url && patch.photo_url !== base.photo_url;
    const q = queued
      ? await ctx.queue.mergeLead(data.lead_id, patch, {
          step: 3,
          sheet_status: "pending",
          last_step_at: new Date().toISOString(),
          drive_status: fallbackCount > 0 || queued.drive_status === "pending" ? "pending" : "done",
          ...(photoUrlChanged && queued.salesforce_status !== "sending" ? { salesforce_status: "pending" } : {}),
        })
      : null;

    let sheetOk = false;
    if (found) {
      try {
        await ctx.sinks.sheet.updateLeadCells(found.rowNumber, patch);
        sheetOk = true;
        if (q && queued?.sheet_status === "done") {
          await ctx.queue.update(data.lead_id, { sheet_status: "done", sheet_error: null });
        }
      } catch (err) {
        if (!q) throw err;
        console.error(`[quickQuote] sheet photo update failed, lead=${data.lead_id}: ${shortErr(err)}`);
        await ctx.queue.sheetFailed(data.lead_id, err);
      }
    }

    // Photos are delivered to Salesforce as Drive links on
    // Patient_Photo_URL__c — no email to client relations.
    // When the browser uploads photos one per request it sets `deliver` only on
    // the last one, so the lead is posted once with every link instead of once
    // per photo. Legacy callers that omit the flag still deliver immediately.
    if (data.deliver === false) {
      return { ok: true as const, photo_urls: uploadedUrls, queued_photos: fallbackCount, delivery: "deferred" as const };
    }

    const result = await deliverLead(data.lead_id, sheetOk, queued, ctx);

    return {
      ok: true as const,
      photo_urls: uploadedUrls,
      queued_photos: fallbackCount,
      delivery: result.outcome,
    };
  });

/**
 * Skip the photo upload and deliver the lead now.
 */
export const deliverQuickQuoteWithoutPhoto = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    friendlyParse(
      z.object({ lead_id: z.string().uuid(), tracking: trackingSchema }),
      data,
    ),
  )
  .handler(async ({ data }) => {
    const ctx = await leadContext();
    if (!ctx) return { ok: true as const, delivery: "skipped" as const };

    const { found, queued, rescued } = await locateLead(data.lead_id, ctx);
    // Only the R2 rescue holds this lead (D1 and the Sheet still down): the
    // drain delivers it, with the address it has. Nothing to add here.
    if (!found && !queued && rescued) return { ok: true as const, delivery: "deferred" as const };
    const tracking = mergeTracking(found?.lead.tracking ?? queued?.lead?.tracking, data.tracking);
    const patch = { ...trackingCells(tracking), tracking: JSON.stringify(tracking) };
    if (queued) await ctx.queue.mergeLead(data.lead_id, patch, { last_step_at: new Date().toISOString() });

    let sheetOk = false;
    if (found) {
      try {
        await ctx.sinks.sheet.updateLeadCells(found.rowNumber, patch);
        sheetOk = true;
      } catch (err) {
        if (!queued) throw err;
      }
    }

    // No new data to send — never resend. If the row was already delivered
    // (flush job or an earlier call) this is a no-op, which keeps Salesforce
    // from receiving a second copy of the same lead.
    const result = await deliverLead(data.lead_id, sheetOk, queued, ctx);

    return { ok: true as const, delivery: result.outcome };
  });

function shortErr(err: unknown): string {
  return (err instanceof Error ? err.message : String(err)).slice(0, 300);
}

function idList(raw: string | undefined | null): string[] {
  return (raw ?? "").split(",").map((v) => v.trim()).filter(Boolean);
}

function mergeTracking(stored: string | undefined, fresh: Record<string, string> | undefined) {
  let tracking: Record<string, string> = {};
  try {
    tracking = stored ? JSON.parse(stored) : {};
  } catch {
    tracking = {};
  }
  return { ...tracking, ...(fresh ?? {}) };
}

/**
 * Where this request's lead goes (6 Oct 2026).
 *  - null: plain test mode (valid x-rh-test, no x-rh-fault). The caller
 *    returns its made-up answer as before and touches nothing.
 *  - fault test (valid x-rh-test AND x-rh-fault): the D1 test tables, the
 *    test/ R2 prefix and the stand-in sinks, with the named sinks failing.
 *  - a non-production host: the same test path, with Drive down (see below).
 *  - otherwise: the real Sheet, Drive and Salesforce, and the D1 queue when
 *    its binding exists.
 * x-rh-fault is read only after the test secret matched, so a visitor can
 * never switch a fault on.
 */
async function leadContext(): Promise<DeliverCtx | null> {
  const { openQueue } = await import("./quick-quote-queue.server");
  const { liveSinks } = await import("./quick-quote-sinks.server");
  if (isQuickQuoteTest()) {
    const faultHeader = getRequestHeader("x-rh-fault");
    if (faultHeader === undefined || faultHeader === null) return null;
    const { parseFaults, testSinks } = await import("./quick-quote-sinks.server");
    const { rawQueueDb } = await import("./quick-quote-queue.server");
    const faults = parseFaults(faultHeader);
    return { sinks: testSinks(rawQueueDb(), faults), queue: openQueue({ test: true, faults }) };
  }
  // A non-production host (a branch preview) with no valid x-rh-test is
  // still a test (8 Oct 2026). A phone cannot send a header. It runs the
  // fault-test path with Drive marked down, so each photo goes to the R2
  // test/ prefix and the qq_test_photos index, and the lead goes to
  // qq_test_queue. The stand-in sinks write only to D1. The sinks also check
  // the host themselves (assertProductionSink), so this is not the only guard.
  if (isPreviewHost()) {
    const { previewFaults, testSinks } = await import("./quick-quote-sinks.server");
    const { rawQueueDb } = await import("./quick-quote-queue.server");
    const faults = previewFaults(getRequestHeader("x-rh-fault"));
    return { sinks: testSinks(rawQueueDb(), faults), queue: openQueue({ test: true, faults }) };
  }
  return { sinks: await liveSinks(), queue: openQueue() };
}

/**
 * Find a lead in the Sheet and in the D1 queue (6 Oct 2026). Either one is
 * enough to carry on. With neither, it fails exactly as before: the Sheet
 * error if the Sheet threw, otherwise "couldn't find your quote".
 */
async function locateLead(
  leadId: string,
  ctx: DeliverCtx,
): Promise<{ found: FoundLead | null; queued: QueueRow | null; rescued: RescueRecord | null }> {
  let found: FoundLead | null = null;
  let sheetError: unknown = null;
  try {
    found = await ctx.sinks.sheet.findLead(leadId);
  } catch (err) {
    sheetError = err;
  }
  let queued = await ctx.queue.get(leadId);
  if (!found && !queued) {
    // Step 1 may have been rescued to R2 (14 Oct 2026). Move it into D1 now
    // if D1 is back, so this step runs the normal way; otherwise hand the
    // rescue record to the caller.
    const { promoteNow, openRescue } = await import("./lead-rescue.server");
    const { rawQueueDb } = await import("./quick-quote-queue.server");
    const rescue = openRescue({ test: ctx.sinks.test, faults: requestFaults(ctx) });
    const rescued = await rescue.get(leadId);
    if (rescued) {
      if (ctx.queue.available && (await promoteNow(rescue, rawQueueDb(), rescued))) {
        queued = await ctx.queue.get(leadId);
      }
      if (!queued) return { found: null, queued: null, rescued };
      return { found: null, queued, rescued: null };
    }
    if (sheetError) throw sheetError;
    throw new Error("We couldn't find your quote. Please start again.");
  }
  return { found, queued, rescued: null };
}

const COULD_NOT_SAVE_DETAILS = "Could not save your details. Please try again, or call (866) 488-9356.";
const COULD_NOT_SAVE_ADDRESS = "Could not save your address. Please try again, or call (866) 488-9356.";

/** Delivery cells only the delivery code writes; never carried into a rescue. */
const BOOKKEEPING_CELLS = [
  "salesforce_status",
  "salesforce_attempts",
  "salesforce_response",
  "salesforce_response_body",
  "sent_signature",
  "sent_at",
];

function withoutBookkeeping(cells: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(cells)) {
    if (!BOOKKEEPING_CELLS.includes(k) && v !== undefined && v !== null) out[k] = String(v);
  }
  return out;
}

/** The faults this request runs with: test and preview requests only. */
function requestFaults(ctx: DeliverCtx): Set<string> {
  if (!ctx.sinks.test) return new Set();
  const header = getRequestHeader("x-rh-fault");
  return new Set([...(isPreviewHost() && !isQuickQuoteTest() ? ["drive"] : []), ...parseFaultNames(header)]);
}

function parseFaultNames(header: string | null | undefined): string[] {
  return (header ?? "").split(",").map((f) => f.trim().toLowerCase()).filter(Boolean);
}

/**
 * Keep a lead that neither D1 nor the Sheet took in the R2 rescue store and
 * alert. If R2 fails too, log LOST, alert with the contact details (the alert
 * is then the only record) and throw `message` to the visitor.
 */
async function rescueOrThrow(
  ctx: DeliverCtx,
  record: Omit<RescueRecord, "v" | "updated_at">,
  message: string,
): Promise<void> {
  const { openRescue, alertCapture, captureLost } = await import("./lead-rescue.server");
  const { rawQueueDb } = await import("./quick-quote-queue.server");
  const rescue = openRescue({ test: ctx.sinks.test, faults: requestFaults(ctx) });
  if (await rescue.put(record)) {
    await alertCapture({ event: "rescued", kind: "qq", leadId: record.id, failures: record.failures }, ctx.sinks.test, rawQueueDb());
    return;
  }
  const failures = [...record.failures, "r2: rescue write failed"];
  captureLost("qq", record.id, failures, ctx.sinks.test);
  const c = record.cells ?? {};
  await alertCapture(
    {
      event: "lost",
      kind: "qq",
      leadId: record.id,
      failures,
      contact: {
        name: [c.first_name, c.last_name].filter(Boolean).join(" "),
        email: c.email,
        phone: c.phone,
        zip: c.zip,
      },
    },
    ctx.sinks.test,
    rawQueueDb(),
  );
  throw new Error(message);
}

/**
 * Post the lead to Salesforce. Through the Sheet when the Sheet is up and
 * holds everything the queue holds (deliverQuickQuoteLead, unchanged rules).
 * When the Sheet is down or behind, first try to bring it up to date; if that
 * fails, post from the queue so Salesforce never waits on the Sheet.
 */
async function deliverLead(leadId: string, sheetOk: boolean, queued: QueueRow | null, ctx: DeliverCtx) {
  const { deliverQuickQuoteLead, deliverQueuedLead } = await import("./quick-quote-deliver.server");
  // `queued` is the row as it was before this step: sheet_status "done"
  // means every earlier step reached the Sheet too.
  let viaSheet = sheetOk && (!queued || queued.sheet_status === "done");
  if (!viaSheet && queued) {
    try {
      const { syncSheetFromQueue } = await import("./quick-quote-replay.server");
      viaSheet = !!(await syncSheetFromQueue((await ctx.queue.get(leadId)) ?? queued, ctx));
    } catch {
      viaSheet = false;
    }
  }
  if (viaSheet || !queued) return deliverQuickQuoteLead(leadId, undefined, ctx);
  return deliverQueuedLead(leadId, ctx);
}
